import { useMemo, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, ToolCase, Hash, Square } from "lucide-react";
import type { MCPTool } from "@/types";
import { ToolsToggle } from "@/components/ui/tools-toggle";

interface MessageComposerProps {
  value: string;
  onValueChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  tools?: MCPTool[];
  onToolsChange?: (tools: MCPTool[]) => void;
  onToolsOpen?: () => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  onStop?: () => void;
  isStreaming?: boolean;
}

export const MessageComposer = ({
  value,
  onValueChange,
  onSend,
  disabled,
  tools = [],
  onToolsChange,
  onToolsOpen,
  selectedTags,
  onTagsChange,
  onStop,
  isStreaming,
}: MessageComposerProps) => {
  const [showTools, setShowTools] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const enabledCount = useMemo(() => tools.filter(tool => tool.enabled !== false).length, [tools]);
  const suggestedTags = useMemo(
    () => tools.map(tool => tool.name).filter(Boolean).slice(0, 6),
    [tools],
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!!!value?.trim()) return;
    onSend();
    setShowTools(false);
  };

  const addTag = (tag: string) => {
    const normalized = tag.trim().replace(/^#/, "");
    if (!normalized) return;
    if (selectedTags.includes(normalized)) {
      setTagDraft("");
      return;
    }
    onTagsChange([...selectedTags, normalized]);
    setTagDraft("");
  };

  const removeTag = (tag: string) => onTagsChange(selectedTags.filter(current => current !== tag));

  return (
    <form
      onSubmit={handleSubmit}
      className="glass-card border border-white/60 p-2 sm:p-3 shadow-md transition duration-150 focus-within:ring-2 focus-within:ring-primary/50 dark:border-white/10"
    >
      <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 sm:gap-3">
        <div className="flex-1 space-y-1.5 sm:space-y-2">
          <Textarea
            data-testid="message-composer-input"
            value={value}
            onChange={event => onValueChange(event.target.value)}
            placeholder="Tell the MCP agent what you need..."
            rows={3}
            className="min-h-[60px] sm:min-h-[80px] resize-none border-none bg-white/60 shadow-sm focus-visible:ring-2 focus-visible:ring-primary/30 dark:bg-white/5 text-base sm:text-sm [font-size:max(16px,1rem)]"
            onKeyDown={event => {
              if (event.ctrlKey && event.key === "Enter") {
                event.preventDefault();
                if (value?.trim() && !disabled) {
                  onSend();
                  onValueChange(""); // Immediately clear input for better UX
                }
              }
            }}
          />
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <div className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 sm:py-1 text-xs text-muted-foreground">
              <Hash className="h-3 w-3" />
              <input
                className="bg-transparent focus:outline-none w-16 sm:w-20 [font-size:max(16px,0.75rem)]"
                value={tagDraft}
                placeholder="Add tag"
                onChange={e => setTagDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag(tagDraft);
                  }
                }}
              />
            </div>
            <Button type="button" size="sm" variant="ghost" onClick={() => addTag(tagDraft)} disabled={!!!tagDraft?.trim()} className="h-7 px-2 text-xs">
              Add
            </Button>
            <span className="hidden sm:inline text-[10px] sm:text-[11px] text-muted-foreground">
              Keep prompts short and stack tools as needed. <span className="font-mono text-orange-400">Ctrl+Enter to send.</span>
            </span>
            <span className="sm:hidden text-[9px] text-muted-foreground font-mono text-orange-400">
              Ctrl+Enter
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {selectedTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => removeTag(tag)}
                className="pill bg-primary/10 text-primary transition hover:bg-primary/20 text-[10px] sm:text-xs px-2 py-0.5"
              >
                #{tag} ✕
              </button>
            ))}
            {!selectedTags.length && suggestedTags.length ? (
              <div className="flex items-center gap-1 text-[10px] sm:text-[11px] text-muted-foreground">
                <Square className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                <span className="hidden sm:inline">Suggestions:</span>
                {suggestedTags.slice(0, 3).map(tag => (
                  <button
                    key={tag}
                    type="button"
                    className="underline decoration-dotted"
                    onClick={() => addTag(tag)}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="relative flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
          <Button
            aria-keyshortcuts="Control+Alt+T"
            name="tools-toggle"
            type="button"
            variant="outline"
            className="gap-1.5 sm:gap-2 flex-1 sm:flex-initial h-9 text-xs sm:text-sm"
            onClick={async () => {
              if (onToolsOpen) onToolsOpen();
              setShowTools(prev => !prev);
            }}
            value={tools.length ? tools.length : 0}
          >
            <ToolCase className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-description="Tools" />
            <span className="hidden sm:inline">Tools {tools.length ? `(${enabledCount}/${tools.length})` : ""}</span>
            <span className="sm:hidden">{enabledCount}/{tools.length}</span>
          </Button>
          {showTools ? (
            <div className="absolute right-0 bottom-12 z-20">
              <ToolsToggle
                tools={tools}
                onChange={next => {
                  onToolsChange?.(next);
                  setShowTools(false);
                }}
              />
            </div>
          ) : null}
          {isStreaming ? (
            <Button type="button" variant="secondary" onClick={onStop} disabled={!onStop} className="flex-1 sm:flex-initial h-9 text-xs sm:text-sm">
              Stop
            </Button>
          ) : null}
          <Button aria-keyshortcuts="Control+Enter" type="submit" disabled={disabled || !!!value?.trim()} className="gap-1.5 sm:gap-2 flex-1 sm:flex-initial h-9 text-xs sm:text-sm">
            <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Send
          </Button>
        </div>
      </div>
    </form>
  );
};
